import {
  DEPLOY_STATUS_PATH,
  PERMISSIONS,
  type PermissionCode,
} from '@asyagrup/group-hub-contracts';
import {
  Banknote,
  BedDouble,
  BookUser,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  DatabaseZap,
  CalendarRange,
  ChartColumn,
  ChartLine,
  ClipboardCheck,
  Contact,
  Scale,
  FileCheck,
  GraduationCap,
  HandCoins,
  Handshake,
  History,
  IdCard,
  Inbox,
  Landmark,
  LayoutDashboard,
  MailCheck,
  Megaphone,
  MessagesSquare,
  LifeBuoy,
  Library,
  ListOrdered,
  Network,
  NotebookPen,
  Percent,
  Plane,
  PlaneTakeoff,
  Plug,
  Receipt,
  Repeat,
  ScrollText,
  ShieldAlert,
  Rocket,
  UserCheck,
  ShieldCheck,
  SprayCan,
  Stamp,
  Target,
  Package,
  Users,
  Wrench,
  type LucideIcon,
  PackageCheck,
  PackageSearch,
  ClipboardList,
  ReceiptText,
  FileUp,
} from 'lucide-react';
import { KATEGORILER_ADRESI } from '@/features/announcements/duyuru-sekmesi';
import { YENILIKLER_ADRESI } from '@/features/support/destek-sekmesi';

/**
 * Menu sections.
 *
 * Fourteen flat entries stopped being a menu and became a list to read. The
 * grouping is by the QUESTION a person arrived with — "what about me?", "what
 * about my people?", "what about the money?" — not by which module the code
 * lives in, because nobody navigates by module.
 */
export type NavSection =
  | 'self'
  | 'workplace'
  | 'people'
  | 'guesthouse'
  | 'payments'
  | 'ledger'
  | 'recurring'
  | 'reports'
  | 'fleet'
  | 'admin';

export const NAV_SECTIONS: Array<{ key: NavSection; label: string }> = [
  // Bölümler "hangi soruyla geliniyor" diye ayrıldı, "hangi departmana ait"
  // diye değil. Öncesinde `money` tek başına 17 girdi taşıyordu — menünün
  // %40'ı — ve içinde rapor, onay akışı, cari, kasa, sabit gider, vergi, uçak
  // ve bakım aynı torbadaydı. Bir kişi "bu faturayı kim onaylayacak?" ile
  // "bu birim kâr etti mi?" sorusunu aynı başlığın altında arıyordu.
  { key: 'self', label: '' },
  // Herkese açık, paraya bakmayan günlük işler. `self` bunları taşıyordu ve
  // etiketsiz dokuz satır, üstteki dördünün kişisel olduğunu görünmez kılıyordu.
  { key: 'workplace', label: 'Kurum içi' },
  { key: 'people', label: 'İnsan kaynakları' },
  // İnsanlarla finans arasında: misafirhane bir işletme, muhasebe kalemi değil.
  // Oraya giden kişi "öğrenci nerede kalıyor?" diye gelir, "kasada ne var?" diye
  // değil — ve o soru finansın altında aranmaz.
  { key: 'guesthouse', label: 'Misafirhane' },
  // Buradan sonrası eski `money`. Dördü de para, ama dört ayrı soru:
  // ne onaylayacağım · kime borçluyuz, kasada ne var · her ay ne çıkıyor · ne oldu.
  { key: 'payments', label: 'Ödeme ve onay' },
  { key: 'ledger', label: 'Cari ve kasa' },
  { key: 'recurring', label: 'Sabit gider ve vergi' },
  { key: 'reports', label: 'Raporlar' },
  // Uçak ve bakım para değil varlık: kiralık uçak bir sözleşme, bakım işleri bir
  // iş listesi. Finansta durduklarında ikisi de gider kalemi gibi okunuyordu.
  { key: 'fleet', label: 'Filo ve bakım' },
  { key: 'admin', label: 'Yönetim' },
];

export interface NavItem {
  key: string;
  to: string;
  label: string;
  /**
   * Typed against the same enum the backend guard uses, so a renamed or deleted
   * permission is a compile error on BOTH sides at once. asya-bakim hand-maintains
   * role arrays per nav item, duplicating the backend mapping; that duplication
   * is what this removes.
   *
   * The check is UX only — the API is the authority, and every route it serves
   * re-checks the permission.
   */
  requires: PermissionCode[];
  section: NavSection;
  /**
   * Required on purpose: an entry without an icon is a compile error (TS2741)
   * here at the definition site, not an empty 36px row in the collapsed rail
   * that nobody notices until someone tries to aim at it.
   *
   * This does put a rendering concern in the permission contract, which is the
   * argument that kept icons in a side table (`NAV_ICONS`) for a while — the
   * worry being that changing a glyph means editing a file where a typo is an
   * authorization change. That argument lost on evidence: the side table
   * drifted twice, first written against 20 entries while this file had 21, and
   * again when three Sabit Giderler entries landed and their icons did not.
   * Both times the menu shipped with blank rows and nothing failed. A required
   * field cannot drift from the list it belongs to; a `Record` keyed by string
   * always can.
   *
   * Note when adding one: lucide-react 0.469.0 has taken the rename. `ChartLine`
   * exists and `LineChart` does not; `House` exists and `Home` does not;
   * `FileSignature` does not exist at all. A name written from memory will not
   * compile — which is now the point.
   */
  icon: LucideIcon;
  /**
   * Screens that arrive in a later milestone stay visible but disabled.
   *
   * No entry sets this `false` today. The rendering branch in `SideNav` is
   * kept for the next milestone rather than pruned as dead code — the
   * reasoning is at its call site.
   */
  ready: boolean;
  /**
   * Optional live counter on the row. OPTIONAL IS THE WHOLE DESIGN: every entry
   * that does not want one is untouched, and stays untouched when a second
   * counter appears.
   *
   * A NAME, never a number. `nav.ts` is a static module — it cannot hold a
   * count, and a count passed through here would make the menu re-render on
   * every poll. The name tells `SideNav` which leaf component to mount; that
   * leaf owns the query and re-renders one `<span>`.
   *
   * Onaylarım's badge predates this field and still hangs off `item.key` in
   * `SideNav`. It is not migrated here on purpose: moving it would be an edit
   * to a row this change has no business touching, in the file every session
   * in this repo edits.
   */
  badge?: 'unread-messages';
  /**
   * Tek satırın arkasında, kapıları AYRI iki ya da daha çok yarısı olan ekran.
   *
   * İsteğe bağlı, ve bugün tek kullanıcısı «Destek ve Yenilikler»: satır
   * herkese görünüyor (`requires: []`) çünkü Yenilikler herkesin, ama Destek
   * yarısı `support.read_own` istiyor ve kapı sayfanın içinde. `requires`
   * yalnız satırın görünürlüğünü anlatabiliyor; yarıların kapısı burada.
   *
   * İki okuyucusu var, ikisi de bu alan yokken yanlış cevap veriyordu:
   *  - Roller ekranı (`page-access.ts`) satırı «herkese açık, kapısı yok»
   *    sanıyor ve Destek yarısının kutucuğunu hiç çizmiyordu.
   *  - ⌘K'nin Ekranlar katmanı (`matchScreens`) «yenilik» yazana satırın
   *    `to`sunu — Destek yarısını — açıyordu.
   *
   * `href` bir yarının adresi; `to` gibi rota denetiminden geçmiyor, çünkü
   * sorgu taşıyabiliyor (`/destek?sekme=yenilikler`) ve yolu zaten `to` ile
   * aynı.
   */
  sections?: ReadonlyArray<{ label: string; href: string; requires: PermissionCode[] }>;
}

/**
 * Turkish URLs: the users are Turkish and the address bar is user-facing.
 *
 * Icons are chosen for scent, not decoration. A wrong icon is worse than none —
 * it teaches a location that isn't there — so the non-obvious ones are argued
 * at the entry they belong to. The rules that span entries:
 *
 *  - `ClipboardCheck` for Onaylarım, not `CheckCheck`: a sheet handed to you to
 *    sign off. `CheckCheck` reads "done", the opposite of a waiting queue.
 *  - `CalendarDays` for İzinlerim, not `CalendarCheck`: the page holds pending
 *    and rejected requests too, and a tick claims they were approved.
 *  - `Network` for Şirket ve Departman, not `Building2`: `Building2` is the
 *    brand mark in the header, and reusing it makes the logo look like a row.
 *  - `ChartColumn` for Birim Kârlılığı, not `TrendingUp`: profitability can be
 *    negative, and an arrow that only points up is a claim the data can't back.
 *  - `BookUser` for Kullanıcılar: accounts, not people. `Users` is Personel and
 *    `UserCog` is the header profile link — both are already spoken for.
 */
export const NAV: NavItem[] = [
  {
    key: 'dashboard',
    to: '/',
    label: 'Panel',
    requires: [PERMISSIONS.DASHBOARD_SELF],
    section: 'self',
    icon: LayoutDashboard,
    ready: true,
  },
  {
    key: 'my-leave',
    to: '/izinlerim',
    label: 'İzinlerim',
    requires: [PERMISSIONS.LEAVE_REQUEST_CREATE],
    section: 'self',
    icon: CalendarDays,
    ready: true,
  },
  {
    // "Bordrolarım" — DST-00092. `self` bölümünde ve Zimmetlerim'in yanında,
    // çünkü aynı soruyu soruyor: "bende ne var". İzni `payslip.read_own`, her
    // rolde var; bu bir İK ekranı değil, kişinin kendi kaydı.
    key: 'my-payslips',
    to: '/bordrolarim',
    label: 'Bordrolarım',
    requires: [PERMISSIONS.PAYSLIP_READ_OWN],
    section: 'self',
    icon: ReceiptText,
    ready: true,
  },
  {
    // "Zimmetlerim" İzinlerim'in yanında ve `people` bölümünde DEĞİL: buraya
    // gelen kişi "üzerimde ne var, neyi teslim aldım" diye gelir, "kimde ne
    // var" diye değil. İkisi ayrı sorular ve ayrı yetkiler — bu satır
    // `asset.read_own` istiyor, ki onu her rol taşıyor (DST-00065).
    key: 'my-assets',
    to: '/zimmetlerim',
    label: 'Zimmetlerim',
    requires: [PERMISSIONS.ASSET_READ_OWN],
    section: 'self',
    icon: PackageCheck,
    ready: true,
  },
  {
    key: 'approvals',
    to: '/onaylarim',
    label: 'Onaylarım',
    requires: [PERMISSIONS.LEAVE_REQUEST_APPROVE],
    section: 'self',
    icon: ClipboardCheck,
    ready: true,
  },
  {
    // 'self' bölümünde: buraya gelen kişi "ben ne yapmıştım" diye gelir. Bir
    // destek talebinden doğdu — kullanıcı bir kaydı açtığından emin olamayıp
    // bize sordu, ve cevabı verecek veri zaten sistemde duruyordu.
    //
    // `History`, `Clock` değil: saat "ne zaman"ı gösterir, buradaki soru "ne
    // oldu". `ScrollText` zaten Denetim Kaydı'nın — o başkalarının kayıtlarını
    // gösteren ayrı bir ekran ve ayrı bir izin.
    key: 'my-history',
    to: '/islemlerim',
    label: 'İşlem Geçmişim',
    requires: [PERMISSIONS.DASHBOARD_SELF],
    section: 'self',
    icon: History,
    ready: true,
  },
  {
    // 'workplace' bölümünde, çünkü buraya gelen kişi "bende şu bozuk" ya da
    // "ne değişti" diye gelir — bir yönetim işi olarak değil. Menüde 'Yönetim'
    // altına konsaydı, bildirim yapması beklenen çoğu kişi orayı hiç açmadığı
    // için modül boş kalırdı.
    key: 'support',
    to: '/destek',
    /*
     * Destek ve Yenilikler TEK SATIR — eskiden iki satırdı, ikisi tek ekranda
     * birleşti (sekmeler: Destek, Yenilikler; eski `/yenilikler` adresi
     * Yenilikler sekmesine yönleniyor).
     *
     * Etikette «Yenilikler» GEÇMEK ZORUNDA — DST-00093'ün dersi (aşağıda
     * «Masraf ve Taleplerim»): tanıdığı ad menüden kaybolan kişi ekranı
     * kaybettiğini sanıyor. Arama bulur; bulunamayan şey GÖZLE bulunması.
     */
    label: 'Destek ve Yenilikler',
    /**
     * Kapısız, bilerek — ve boş dizi bunun burada söylenme biçimi.
     *
     * `requires.every(can)` boş liste için her zaman doğru, yani satır herkese
     * görünüyor. Yenilikler yarısı herkesin: ürünün neyin değiştiği kimsenin
     * özel işi değil, ve yalnız bazı rollerin okuyabildiği bir sürüm notu
     * insanların birbirine sorduğu bir sürüm notudur. Destek kayıtları
     * `support.read_own` istiyor, ama o kapı SAYFANIN İÇİNDE: izni olmayan
     * biri satıra tıklayınca yalnız Yenilikler'i görüyor.
     */
    requires: [],
    // Yarıların kapısı — Roller ekranı ve ⌘K buradan okuyor (`sections`).
    sections: [
      { label: 'Destek', href: '/destek', requires: [PERMISSIONS.SUPPORT_READ_OWN] },
      { label: 'Yenilikler', href: YENILIKLER_ADRESI, requires: [] },
    ],
    section: 'workplace',
    icon: LifeBuoy,
    ready: true,
  },
  {
    // 'self' bölümünde, 'people' altında değil: buraya gelen kişi "şu formu
    // arıyorum" diye gelir, bir İK işi yapmaya değil. Yönetmeliği okuması
    // beklenen herkes bu satırı görmeli, ve İnsan kaynakları başlığının altını
    // çoğu kişi hiç açmaz.
    key: 'document-library',
    to: '/dokumanlar',
    label: 'Doküman Kütüphanesi',
    requires: [PERMISSIONS.DOCUMENT_LIBRARY_READ],
    section: 'workplace',
    // `Library`, `Files` ya da `FolderOpen` değil: ikisi de "bir yerde birkaç
    // dosya var" diyor, bu ekran ise düzenlenmiş ve sürümlenmiş bir koleksiyon.
    // `ScrollText` zaten Denetim Kaydı'nın.
    icon: Library,
    ready: true,
  },
  {
    // Mesajlar ve Duyuruların ÜSTÜNDE: bu satır o iki ekranın kapısı, kardeşi
    // değil. Kendi tablosu ve kendi listesi yok — altı bölümün "bana ne
    // bekliyor" sayısını gösterip sahibine bağlıyor. Yetkisi announcement.read,
    // /duyurular ile aynı ve aynı gerekçeyle (SELF_PERMISSIONS'ta, yani
    // herkeste); yetkisi olmayan bölüm ekranda hiç çizilmiyor, sıfır yazmıyor.
    key: 'communication',
    to: '/iletisim',
    label: 'İletişim Merkezi',
    requires: [PERMISSIONS.ANNOUNCEMENT_READ],
    section: 'workplace',
    // `Inbox`: gelen kutusu. `Megaphone` Duyuruların, `MessagesSquare`
    // Mesajların ve `Bell` bu menüde hiç yok — zil kabuğun sağ üstünde.
    icon: Inbox,
    ready: true,
  },
  {
    // 'self' bölümünde: buraya gelen kişi kendisine ne bildirildiğine bakmaya
    // gelir, bir yönetim işi yapmaya değil — ve tebliğ okuması beklenen herkes
    // bu satırı görmeli. Yetkisi announcement.read; yayım ve makbuz kendi
    // kodlarını ekranın içinde soruyor.
    key: 'announcements',
    to: '/duyurular',
    label: 'Duyurular',
    requires: [PERMISSIONS.ANNOUNCEMENT_READ],
    // Kategori sözlüğü bu ekranın son sekmesi; eskiden kendi satırıydı
    // («Duyuru Kategorileri»). Yarılar burada ki Roller ekranı sözlüğü
    // `announcement.category_manage` kutucuğuyla AYRI satır olarak göstermeye
    // devam etsin — satır kalkınca o kutucuk sayfa listesinden düşüyordu.
    sections: [
      { label: 'Duyuru ve memorandumlar', href: '/duyurular', requires: [] },
      {
        label: 'Kategoriler',
        href: KATEGORILER_ADRESI,
        requires: [PERMISSIONS.ANNOUNCEMENT_CATEGORY_MANAGE],
      },
    ],
    section: 'workplace',
    // `Megaphone`, çünkü "birine doğru duyurur" — ve bu modülün yaptığı tam
    // olarak budur. (Sürüm notları bu yüzden onu hiç kullanmadı: «burada yeni
    // bir şey var» demek bir duyuru değil.)
    icon: Megaphone,
    ready: true,
  },
  {
    // Yetkisi message.use ve o kod SELF_PERMISSIONS'ta: bir meslektaşına
    // yazmak için izin istemek, yazışmayı sistemin dışına — saklama
    // politikası olmayan bir yere — taşır. Grup ve kanal açma daha dar
    // kodlarda ve ekranın içinde soruluyor; menü satırı hepsinde aynı.
    key: 'messaging',
    to: '/mesajlar',
    label: 'Mesajlar',
    requires: [PERMISSIONS.MESSAGE_USE],
    section: 'workplace',
    // `MessagesSquare`, `Megaphone` DEĞİL: iki ekran yan yana duruyor ve
    // Duyurular tek yönlü bir yayım, bu ise karşılıklı bir konuşma. Tekil
    // `MessageSquare` de değil — ikisi bir listede birbirinden ayırt edilmez
    // ve çoğul biçim "birden çok konuşma" diyor, ki ekranın kendisi budur.
    icon: MessagesSquare,
    ready: true,
    // Okunmamış konuşma sayısı. Sayıyı bu dosya taşımıyor; adı taşıyor.
    badge: 'unread-messages',
  },
  {
    // 'self' bölümünde: buraya gelen kişi "benim günümde ne var" diye gelir.
    // Görevlendirme de aynı sorunun parçası — başkasına verilen bir iş, veren
    // kişinin gününde "takip etmem gereken" bir satırdır.
    //
    // Yetkisi agenda.use, agenda.assign değil: menüyü görevlendirmeye
    // bağlamak, ekranı asıl kullanıcısından — kendi notunu tutan herkesten —
    // gizlerdi.
    key: 'agenda',
    to: '/ajanda',
    label: 'Ajanda',
    requires: [PERMISSIONS.AGENDA_USE],
    section: 'workplace',
    // `NotebookPen`, `CalendarDays` değil: ikincisi İzinlerim'in simgesi ve iki
    // takvim yan yana durduğunda hangisinin hangisi olduğu okunmaz. Bu ekranın
    // İzinlerim'den farkı, takvime bir şey YAZILIYOR olması.
    icon: NotebookPen,
    ready: true,
  },
  {
    // Bordro dağıtımı özlük işinin parçası, o yüzden `people` bölümünde ve
    // `payslip.manage` istiyor. Çalışanın kendi ekranı ayrı ve `self`'te —
    // "kimin bordrosunu yüklüyorum" ile "benim bordrom" ayrı sorular.
    key: 'payslip-upload',
    to: '/bordro',
    label: 'Bordro Yükleme',
    requires: [PERMISSIONS.PAYSLIP_MANAGE],
    section: 'people',
    icon: FileUp,
    ready: true,
  },
  {
    key: 'attendance',
    to: '/puantaj',
    label: 'Puantaj',
    requires: [PERMISSIONS.ATTENDANCE_READ],
    section: 'people',
    icon: CalendarClock,
    ready: true,
  },
  {
    // Puantaj'ın hemen altında, çünkü ikisi de aynı işin iki yarısı: ay boyunca
    // ölçülen bir şeyin ay sonunda okunması. Finans'ta değil — buraya gelen kişi
    // "pilotlarım bu ay ne uçtu?" diye gelir, "kasada ne var?" diye değil; ödeme
    // tarafı zaten ayrı bir ekran ve ay oraya gönderiliyor.
    key: 'flight-premium',
    to: '/ucus-primleri',
    label: 'Uçuş Primleri',
    requires: [PERMISSIONS.FLIGHT_PREMIUM_READ],
    section: 'people',
    // Para simgelerinin hepsi tutulmuş (`Banknote`, `HandCoins`, `Percent`,
    // `Landmark`) ve bu ekranın ayırt edici yanı zaten para değil: primi
    // üreten şey uçuş. `Plane` yerine `PlaneTakeoff` — ikincisi bir olayı
    // gösteriyor, bir nesneyi değil, ve bu tabloda satırlar uçuşlar.
    icon: PlaneTakeoff,
    ready: true,
  },
  {
    // Zimmet Personel'in yanında: eşya kişiye bağlanıyor ve "kimde ne var"
    // sorusu personel dosyasının devamı. Okuma yetkisiyle açılıyor — birim
    // müdürü de kendi ekibinde ne olduğunu görmeli (DST-00065).
    key: 'assets',
    to: '/zimmet',
    label: 'Zimmet',
    requires: [PERMISSIONS.ASSET_READ],
    section: 'people',
    icon: Package,
    ready: true,
  },
  {
    // Kütük ile TUTANAK ayrı girdiler, çünkü ayrı sorular: biri "elimizde ne
    // var", diğeri "kime ne verdik". Tek ekranda birleştirmek, iade alacak
    // kişiyi varlık listesinde tutanak aramaya zorlardı.
    key: 'asset-custodies',
    to: '/zimmet/tutanaklar',
    label: 'Zimmet Tutanakları',
    requires: [PERMISSIONS.ASSET_READ],
    section: 'people',
    icon: ClipboardList,
    ready: true,
  },
  {
    key: 'asset-reports',
    to: '/zimmet/raporlar',
    label: 'Zimmet Raporları',
    requires: [PERMISSIONS.ASSET_REPORT],
    section: 'reports',
    icon: PackageSearch,
    ready: true,
  },
  {
    key: 'employees',
    to: '/personel',
    label: 'Personel',
    requires: [PERMISSIONS.EMPLOYEE_READ],
    section: 'people',
    icon: Users,
    ready: true,
  },
  {
    // Onaylarım bir gelen kutusudur ve bir talep karara bağlandığı an oradan
    // düşer. "Geçen yıl kaç gün izin kullandı" sorusunun cevabı bir liste,
    // dolayısıyla ayrı bir girdi — ve okuma yetkisine bakar, onaylamaya değil.
    key: 'leave-records',
    to: '/izinler',
    label: 'İzin Kayıtları',
    requires: [PERMISSIONS.LEAVE_REQUEST_READ],
    section: 'people',
    icon: CalendarCheck,
    ready: true,
  },
  {
    key: 'guesthouse',
    to: '/misafirhane',
    label: 'Misafirhane',
    requires: [PERMISSIONS.KONAKLAMA_READ],
    section: 'guesthouse',
    icon: BedDouble,
    ready: true,
  },
  {
    key: 'guesthouse-reservations',
    to: '/misafirhane/rezervasyon',
    label: 'Rezervasyonlar',
    requires: [PERMISSIONS.KONAKLAMA_READ],
    section: 'guesthouse',
    icon: CalendarRange,
    ready: true,
  },
  {
    // Tahsilat ve gider aynı ekranda: ikisi de misafirhanenin kasasına bakan
    // aynı kişinin işi, ve ayrı menü girdisi olsalar biri her zaman unutulur.
    key: 'guesthouse-collections',
    to: '/misafirhane/tahsilat',
    label: 'Tahsilat ve Gider',
    requires: [PERMISSIONS.KONAKLAMA_READ],
    section: 'guesthouse',
    icon: HandCoins,
    ready: true,
  },
  {
    // DST-00120. Misafirhane bölümünde, çünkü onu kullanan aynı masa: öğrenciden
    // konaklama parasını alan kişi sınav ve materyal parasının dekontunu da
    // tutuyor. Ayrı izin (okul_tahsilat.read), çünkü ayrı bir para.
    key: 'school-collections',
    to: '/okul-tahsilat',
    label: 'Sınav ve Materyal',
    requires: [PERMISSIONS.OKUL_TAHSILAT_READ],
    section: 'guesthouse',
    icon: GraduationCap,
    ready: true,
  },
  {
    key: 'guesthouse-operations',
    to: '/misafirhane/isletme',
    label: 'Temizlik ve Eksikler',
    requires: [PERMISSIONS.KONAKLAMA_READ],
    section: 'guesthouse',
    icon: SprayCan,
    ready: true,
  },
  {
    key: 'finance',
    to: '/karlilik',
    label: 'Birim Kârlılığı',
    requires: [PERMISSIONS.FINANCE_READ],
    section: 'reports',
    icon: ChartColumn,
    ready: true,
  },
  {
    /**
     * Masraflar ile Personel Talepleri'nin YERİNE geçen tek satır.
     *
     * İki satır aynı cümlenin iki yarısıydı — «cebimden çıkanı geri istiyorum»
     * ve «henüz alınmamış bir şeyi istiyorum» — ve hangisinin nerede olduğunu
     * bilmek kullanıcının işi değil. Ekran `/taleplerim`de birleşti, iki eski
     * adres oraya yönleniyor.
     *
     * `requires` TEK izin, çünkü süzgeç `requires.every(can)`: iki izni birden
     * yazmak, birini taşıyıp öbürünü taşımayan kişiden satırı GİZLERDİ — oysa
     * o kişinin girebileceği bir sekme var. `expense.create` de
     * `staff_request.create` de SELF_PERMISSIONS içinde, yani her çalışanda;
     * ikisinden biri seçilecekse sekmenin varsayılanı olan masraf doğru olanı.
     * Sekme başına kapıyı sayfa kendisi kuruyor.
     */
    key: 'taleplerim',
    to: '/taleplerim',
    /*
     * Etikette «Masraf» GEÇMEK ZORUNDA (DST-00093).
     *
     * 20 Ağustos'ta bu satır «Masraflar» adını bıraktı ve «Personel Talepleri»
     * de içine alındı; iki gün önce de bölüm değiştirmişti. Sonuç: 48 saat
     * içinde satırın yeri değişti, tanıdığı iki ad birden gitti ve menüde
     * «masraf» kelimesi hiç kalmadı. Bildiren kişi ekranı kaybettiğini sandı —
     * oysa yetkisi de satırı da yerindeydi.
     *
     * Arama zaten buluyordu; bulunamayan şey GÖZLE bulunmasıydı. Bu yüzden
     * çözüm adı yine değiştirmek değil, kaybolan kelimeyi geri koymak:
     * «Taleplerim» duruyor, «Masraf» yanına dönüyor.
     */
    label: 'Masraf ve Taleplerim',
    requires: [PERMISSIONS.EXPENSE_CREATE],
    section: 'payments',
    // İki eski simgeden `Receipt` kaldı, `Hand` değil: birleşik satırın
    // varsayılan yarısı masraf ve fiş onun somut karşılığı. `Hand` yalnız
    // «istemek» diyor, yani ekranın bir yarısını anlatır, tamamını değil.
    icon: Receipt,
    ready: true,
  },
  {
    // Finans bölümünde, çünkü buraya gelen kişinin sorusu para: "bitmiş iş
    // duruyor, faturası kesildi mi?". Bakım operasyonu değil — o birimin kendi
    // ERP'sinde, ve orada kalıyor.
    key: 'maintenance-jobs',
    to: '/bakim-isleri',
    label: 'Bakım İşleri',
    requires: [PERMISSIONS.MAINTENANCE_READ],
    section: 'fleet',
    // `ClipboardCheck` semantik olarak daha yakındı ama Onaylarım'da kullanılıyor
    // ve raydaki iki özdeş simge, ikisini de tanınmaz yapar. `Wrench` işin
    // NEREDEN geldiğini söylüyor — finans bölümündeki tek bakım kalemi bu.
    icon: Wrench,
    ready: true,
  },
  {
    key: 'payment-week',
    to: '/odeme-tablosu',
    label: 'Ödeme Tablosu',
    // Filing a payment request is self-service, and following your own through
    // the chain is part of filing it. PAYMENT_READ now means "see everybody
    // else's", so gating the menu on it would hide the screen from the very
    // people who use it most.
    requires: [PERMISSIONS.PAYMENT_REQUEST_CREATE],
    section: 'payments',
    icon: Banknote,
    ready: true,
  },
  {
    // Tablonun hemen ardında: zincir o tablonun kuralı, ayrı bir konu değil.
    // Yetkisi payment.read değil approval_chain_manage — kapıların sırasını ve
    // kapılardaki isimleri değiştirmek, onay vermekten daha ileri bir yetki.
    key: 'payment-approval-chain',
    to: '/odeme-onay-zinciri',
    // Ekran artık iki zinciri birden yönetiyor — ödeme talebi ve fatura. Adında
    // "Ödeme" yazan bir menü öğesi, fatura zincirini kimsenin bakmayacağı bir
    // yerde bırakırdı.
    label: 'Onay Zincirleri',
    requires: [PERMISSIONS.PAYMENT_APPROVAL_CHAIN_MANAGE],
    section: 'payments',
    // The chain is an ordered list of gates — `ListOrdered` says "sequence",
    // which is the whole difference between this screen and Ödeme Tablosu.
    icon: ListOrdered,
    ready: true,
  },
  {
    // Kendi kapısı: cari ekranı `payment_request.create` ile açılıyor ve o izin
    // her çalışanda. Fatura tutarlarını o kapının arkasına koymak, bordro dışı
    // bütün gider akışını herkese açardı.
    key: 'invoice-approval',
    to: '/fatura-onay',
    label: 'Fatura Onayı',
    requires: [PERMISSIONS.INVOICE_READ],
    section: 'payments',
    icon: FileCheck,
    ready: true,
  },
  {
    // Zincirden sonra, kasadan önce: cari, ödeme tablosunun beslendiği kayıt.
    // Yetkisi payee.manage DEĞİL — defteri okumak talebi açan kişinin işinin
    // parçası, ve menüyü yazma yetkisine bağlamak ekranı tam da IBAN'ı elle
    // yazmaktan kurtardığımız kişilerden gizlerdi. Yazma ekranın içinde ayrı.
    // Cari hesap, cari kaydının hemen ardında: aynı firmanın "kim" ve "ne
    // oldu" tarafları. Yetkisi farklı — payment.read, çünkü bakiye kayıt
    // defterinden dar bir çevrenin işi.
    key: 'payee-ledger',
    to: '/cari-hesap',
    label: 'Cari Hesap',
    requires: [PERMISSIONS.PAYMENT_READ],
    section: 'ledger',
    icon: Scale,
    ready: true,
  },
  {
    key: 'payee-register',
    to: '/cari',
    label: 'Cari Kayıtları',
    // Defteri GEZMEK artık onu yönetenlere ait. Talebi açan kişi cariyi ödeme
    // formundaki aramadan buluyor (dört harf), listeyi hiç görmüyor: bir satırı
    // seçmek ile bütün tedarikçi listesini okumak aynı yetki değil.
    requires: [PERMISSIONS.PAYEE_MANAGE],
    section: 'ledger',
    icon: Contact,
    ready: true,
  },
  {
    // Veri Düzeltme Merkezi 'admin' bölümünde: burası bir finans ekranı değil,
    // sistemin kendi kayıtlarını onaran bir araç.
    key: 'veri-duzeltme',
    to: '/yonetim/veri-duzeltme',
    label: 'Veri Düzeltme Merkezi',
    // `Wrench` Bakım İşleri'nde; oradaki yorumun dediği gibi raydaki iki özdeş
    // simge ikisini de tanınmaz yapar. `DatabaseZap` işin NE olduğunu söylüyor:
    // veritabanı kayıtlarına dokunan bir araç.
    icon: DatabaseZap,
    requires: [PERMISSIONS.CORRECTION_READ],
    section: 'admin',
    ready: true,
  },
  {
    // Proje/operasyon boyutu FINANCE_READ'in altında DEĞİL: faturaya proje
    // seçen kişi grubun bütün mali tablosunu görmek zorunda değil.
    key: 'projects',
    to: '/projeler',
    label: 'Proje / Operasyon',
    icon: Network,
    requires: [PERMISSIONS.PROJECT_READ],
    section: 'ledger',
    ready: true,
  },
  {
    // Kasa/banka finance.read değil treasury.read istiyor. Ayın ne tuttuğunu
    // görenle hesapta ne olduğunu gören aynı çevre değil.
    key: 'treasury',
    to: '/kasa-banka',
    label: 'Kasa ve Banka',
    requires: [PERMISSIONS.TREASURY_READ],
    section: 'ledger',
    icon: Landmark,
    ready: true,
  },
  {
    key: 'cash-flow',
    to: '/nakit-akis',
    label: 'Nakit Akış',
    requires: [PERMISSIONS.TREASURY_READ],
    section: 'reports',
    icon: ChartLine,
    ready: true,
  },
  {
    // Okumak finance.read; yazmak budget.manage. Menü okuma yetkisine bakar,
    // çünkü sapmayı görmesi gereken kişi bütçeyi girecek kişi değil.
    key: 'budget',
    to: '/butce',
    label: 'Bütçe',
    requires: [PERMISSIONS.FINANCE_READ],
    section: 'reports',
    icon: Target,
    ready: true,
  },
  {
    // One entry, not two: the contract detail hangs off the fleet list and
    // `isExactRoute` derives the prefix route from this one. The menu asks only
    // for the read permission — entering hours, confirming them and filing a
    // payment are gated by the screen's own controls.
    key: 'leased-aircraft',
    to: '/kiralik-ucak',
    label: 'Kiralık Uçak',
    requires: [PERMISSIONS.LEASED_AIRCRAFT_READ],
    section: 'fleet',
    // `Plane`, not `Handshake`: that one is the fixed-expense contracts row, and
    // what separates these two screens is that this obligation is measured in
    // flight hours rather than in months.
    icon: Plane,
    ready: true,
  },
  {
    // The calendar first, the contracts behind it: the question people arrive
    // with is "what goes out this month", not "what does the lease say". All
    // three ask only for the read permission — the screens gate their own
    // writes, and a menu that hid the loan balances from everyone who cannot
    // rewrite an instalment plan would hide them from everyone who pays.
    key: 'fixed-expense',
    to: '/sabit-giderler',
    label: 'Sabit Giderler',
    requires: [PERMISSIONS.FIXED_EXPENSE_READ],
    section: 'recurring',
    // `Repeat`, not `Receipt`: that is Taleplerim, and the one thing separating
    // the two screens is that these come back every month.
    icon: Repeat,
    ready: true,
  },
  {
    // Finans bölümü zaten uzun, o yüzden verginin TEK girdisi var: takvim.
    // Mükellefler ve yükümlülük türleri katalogu buradan bağlanıyor.
    //
    // Girdi takvim, mükellef listesi DEĞİL: insanların bu modüle geldiği soru
    // "bu ay ne ödeyeceğiz, gecikmiş ne var" — "ASYA'nın vergi dairesi neydi"
    // değil. Mükellef kartı ayda bir açılır, takvime her gün bakılır.
    key: 'tax-calendar',
    to: '/vergi',
    label: 'Vergi Takvimi',
    requires: [PERMISSIONS.TAX_READ],
    section: 'recurring',
    // `Stamp`: tahakkuk fişinin mührü. `Landmark` Kasa ve Banka'da, `Percent`
    // Krediler'de, `Scale` Cari Hesap'ta — raydaki özdeş iki simge ikisini de
    // tanınmaz yapıyor.
    icon: Stamp,
    ready: true,
  },
  {
    key: 'fixed-expense-commitments',
    to: '/sabit-giderler/sozlesmeler',
    label: 'Sabit Gider Sözleşmeleri',
    requires: [PERMISSIONS.FIXED_EXPENSE_READ],
    section: 'recurring',
    // `FileSignature` would be the obvious pick and does not exist in 0.469.0;
    // `FileText` is a generic document and collides in meaning with
    // `ScrollText` on Denetim Kaydı. A sözleşme is an agreement between two
    // parties, which is what this draws.
    icon: Handshake,
    ready: true,
  },
  {
    key: 'fixed-expense-loans',
    to: '/sabit-giderler/krediler',
    label: 'Krediler',
    requires: [PERMISSIONS.FIXED_EXPENSE_READ],
    section: 'recurring',
    // `Landmark` is Kasa ve Banka, `Banknote` is Ödeme Tablosu and `HandCoins`
    // is Tahsilat — all spoken for. `PiggyBank` reads as money saved, the
    // opposite direction. Interest is what a loan has that a contract does not.
    icon: Percent,
    ready: true,
  },
  {
    key: 'org',
    to: '/kurulus',
    label: 'Şirket ve Departman',
    requires: [PERMISSIONS.ORG_UNIT_READ],
    section: 'people',
    icon: Network,
    ready: true,
  },
  {
    // Ünvan, roller ve yetkilerin YANINDA ama onlardan ayrı bir satır: ikisi
    // aynı ekranda toplansa "ünvan eklemek" yetki dağıtmakla karışırdı, ki
    // sistemin en pahalı karışıklığı budur.
    key: 'job-titles',
    to: '/yonetim/unvanlar',
    label: 'Ünvanlar',
    requires: [PERMISSIONS.ORG_UNIT_READ],
    section: 'admin',
    // `IdCard`: ünvan kişinin kartındaki satırdır. `Briefcase` işi, `Award`
    // ödülü anlatır; `ShieldCheck` zaten Roller ve Yetkiler'in.
    icon: IdCard,
    ready: true,
  },
  {
    key: 'users',
    to: '/yonetim/kullanicilar',
    label: 'Kullanıcılar',
    requires: [PERMISSIONS.USER_READ],
    section: 'admin',
    icon: BookUser,
    ready: true,
  },
  {
    key: 'roles',
    to: '/yonetim/roller',
    label: 'Roller ve Yetkiler',
    requires: [PERMISSIONS.ROLE_READ],
    section: 'admin',
    icon: ShieldCheck,
    ready: true,
  },
  {
    // Under Yönetim rather than beside Puantaj: reading a timesheet and
    // rewriting the rule that produced it are different jobs done by different
    // people, and the menu should not suggest otherwise.
    key: 'attendance-policy',
    to: '/yonetim/puantaj-politikasi',
    label: 'Puantaj Politikası',
    requires: [PERMISSIONS.ATTENDANCE_READ],
    section: 'admin',
    icon: CalendarClock,
    ready: true,
  },
  {
    key: 'activity',
    to: '/yonetim/aktivite',
    label: 'Personel Aktivitesi',
    requires: [PERMISSIONS.ACTIVITY_READ],
    section: 'admin',
    // `Activity` is the heartbeat line and reads as monitoring; `Radar` is
    // worse in the same direction. `UserCheck` says what the screen answers —
    // who is here — without implying the system is watching them work.
    icon: UserCheck,
    ready: true,
  },
  {
    // `MailCheck`, `Mail` değil: bu ekran posta göndermez, gönderilecek olanı
    // gösterir — ve zarf ikonu "yeni ileti" okunur.
    key: 'mail-preview',
    to: '/yonetim/eposta-onizleme',
    label: 'E-posta Önizleme',
    requires: [PERMISSIONS.SETTINGS_MANAGE],
    section: 'admin',
    icon: MailCheck,
    ready: true,
  },
  {
    key: 'audit',
    to: '/yonetim/denetim',
    label: 'Denetim Kaydı',
    requires: [PERMISSIONS.AUDIT_READ],
    section: 'admin',
    icon: ScrollText,
    ready: true,
  },
  {
    key: 'security',
    to: '/yonetim/guvenlik',
    label: 'Güvenlik',
    requires: [PERMISSIONS.SECURITY_READ],
    section: 'admin',
    icon: ShieldAlert,
    ready: true,
  },
  {
    // Alarmın (`system.deploy`) gittiği kişilerin ekranı; bildirim de buraya
    // götürüyor (DST-00133).
    key: 'deploy-status',
    to: DEPLOY_STATUS_PATH,
    label: 'Dağıtım Durumu',
    requires: [PERMISSIONS.JOB_READ],
    section: 'admin',
    icon: Rocket,
    ready: true,
  },
  {
    key: 'integration',
    to: '/entegrasyon',
    label: 'Entegrasyon',
    requires: [PERMISSIONS.INTEGRATION_READ],
    section: 'admin',
    icon: Plug,
    ready: true,
  },
];
